// ============================================
// FILE: src/components/ProfileScreen.jsx
// ============================================

import React, { useState } from 'react';
import { User, MapPin, Shield, Heart, Package, Bell, LogOut, ChevronRight, MessageCircle, Phone, Mail, Smartphone } from 'lucide-react';
import PWAScreen from './PWAScreen';

const ProfileScreen = ({
  userProfile,
  setUserProfile,
  handleLogout,
  subscription,
  setSubscription,
  twoFactorEnabled,
  setTwoFactorEnabled,
  showSecuritySettings,
  setShowSecuritySettings,
  loginHistory,
  securityLogs,
  orders,
  favorites,
  setFavorites,
  setShowMyOrders,
  setShowFavorites,
  setShowNotificationsModal,
  setShowPaymentMethods,
  setShowEditAddress,
  setShowEditProfile,  // Add this line
  addSecurityLog,
  deliveryAddresses,
  handleAddAddress,
  handleUpdateAddress,
  handleDeleteAddress,
  handleSetDefaultAddress,
  setShowAddAddress,
  editingAddress,        // Add this
  setEditingAddress,      // Add this
  handlePauseSubscription,
  handleResumeSubscription,
  handleCancelSubscription,
  handleModifySubscription,
  handleReactivateSubscription,
  appSettings,
  loading
}) => {
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showPWAScreen, setShowPWAScreen] = useState(false);

  // Show PWA screen if requested
  if (showPWAScreen) {
    return <PWAScreen onBack={() => setShowPWAScreen(false)} />;
  }

  return (
    <div className="pb-20 min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-b-3xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">My Profile</h2>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-xl transition-all backdrop-blur-sm"
          >
            <LogOut size={18} />
            <span className="text-sm font-medium">Logout</span>
          </button>
        </div>
        <div className="flex items-center">
          <div className="bg-white rounded-full p-1 mr-4">
            {userProfile.profilePic ? (
              <img 
                src={userProfile.profilePic} 
                alt="Profile" 
                className="w-20 h-20 rounded-full object-cover"
              />
            ) : (
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
                <User size={32} className="text-blue-600" />
              </div>
            )}
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">{userProfile.name || 'User'}</h1>
            <p className="text-blue-100">{userProfile.email || 'user@email.com'}</p>
            <button 
              onClick={() => setShowEditProfile(true)}
              className="text-blue-100 text-sm mt-1 hover:underline"
            >
              Edit Profile
            </button>
          </div>
        </div>
      </div>

      <div className="p-4">
        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <button
            onClick={() => setShowMyOrders(true)}
            className="bg-white rounded-xl p-4 text-center shadow-sm hover:shadow-md transition-colors cursor-pointer"
          >
            <Package size={24} className="text-blue-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-800">{orders ? orders.length : 0}</p>
            <p className="text-xs text-gray-500">Orders</p>
          </button>

          <button
            onClick={() => setShowFavorites(true)}
            className="bg-white rounded-xl p-4 text-center shadow-sm hover:shadow-md transition-colors cursor-pointer"
          >
            <Heart size={24} className="text-red-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-800">{favorites ? favorites.length : 0}</p>
            <p className="text-xs text-gray-500">Favorites</p>
          </button>

          <button
            onClick={() => setShowNotificationsModal(true)}
            className="bg-white rounded-xl p-4 text-center shadow-sm hover:shadow-md transition-colors cursor-pointer"
          >
            <Shield size={24} className="text-green-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-800">{userProfile && (userProfile.rewards || userProfile.points || 0)}</p>
            <p className="text-xs text-gray-500">Rewards</p>
          </button>
        </div>

        {/* Delivery Addresses */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-3">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <MapPin size={20} className="text-blue-600" />
            Delivery Addresses
          </h3>
          <button
            onClick={() => {
              setEditingAddress(null);
              setShowAddAddress(true);
            }}
            className="text-blue-600 text-sm font-medium hover:underline"
          >
            + Add New
          </button>
        </div>

        {deliveryAddresses && deliveryAddresses.length > 0 ? (
          <div className="space-y-3">
            {deliveryAddresses.map((addr) => (
              <div
                key={addr.id}
                className={`border-2 rounded-xl p-3 transition-all ${
                  addr.isDefault
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-800">{addr.label}</span>
                    {addr.isDefault && (
                      <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">
                        Default
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setEditingAddress(addr);
                        setShowAddAddress(true);
                      }}
                      className="text-blue-600 text-sm hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteAddress(addr.id)}
                      className="text-red-600 text-sm hover:underline"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                
                <p className="text-sm text-gray-600 mb-1">{addr.address}</p>
                <p className="text-sm text-gray-600">
                  {addr.city}, {addr.state} - {addr.pincode}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Phone: {addr.phone}
                </p>

                {!addr.isDefault && (
                  <button
                    onClick={() => handleSetDefaultAddress(addr.id)}
                    className="text-blue-600 text-sm font-medium mt-2 hover:underline"
                  >
                    Set as Default
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-sm text-center py-4">
            No delivery addresses added yet
          </p>
        )}
      </div>

        {/* Active Subscription */}
        {subscription && ( (Array.isArray(subscription) && subscription.length > 0) || (typeof subscription === 'object' && subscription !== null) ) && (
          (() => {
            const activeSub = Array.isArray(subscription) ? subscription[0] : subscription;
            const statusLower = (activeSub.status || '').toString().toLowerCase();
            return (
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow-sm p-4 mb-3 border-2 border-green-200">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                      <Bell size={20} className="text-green-600" />
                      Active Subscription
                    </h3>
                    <p className="text-sm text-green-700 font-medium mt-1">{activeSub.product?.name || activeSub.productName || 'Subscription'}</p>
                  </div>
                  <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                    {(activeSub.status || '').toString().toUpperCase()}
                  </span>
                </div>
                <div className="space-y-1 mb-3">
                  <p className="text-sm text-gray-600">Plan: {activeSub.plan?.name || activeSub.planName || activeSub.frequency || '—'}</p>
                  <p className="text-sm text-gray-600">Started: {activeSub.startDate || activeSub.created_at || ''}</p>
                  <p className="text-sm text-gray-600">
                    Delivery: {activeSub.deliverySlot === 'morning' ? '🌅 Morning (6-8 AM)' : activeSub.deliverySlot}
                  </p>
                  <p className="text-sm text-green-600 font-medium">
                    Saving {activeSub.plan?.discount || activeSub.planDiscount || 0}% on every delivery!
                  </p>
                </div>
                <div className="flex gap-2">
                  {statusLower === 'cancelled' || statusLower === 'canceled' ? (
                    <>
                      <button
                        onClick={() => handleReactivateSubscription && handleReactivateSubscription(activeSub.id)}
                        className="text-blue-600 text-sm font-medium bg-white px-3 py-2 rounded-lg hover:bg-blue-50 transition-all">
                        Reactivate Subscription
                      </button>
                      <button
                        onClick={() => typeof handleModifySubscription === 'function' && handleModifySubscription(activeSub)}
                        className="text-blue-600 text-sm font-medium bg-white px-3 py-2 rounded-lg hover:bg-blue-50 transition-all">
                        Modify Plan
                      </button>
                    </>
                  ) : statusLower === 'paused' || statusLower === 'inactive' ? (
                    <>
                      <button
                        onClick={() => handleResumeSubscription && handleResumeSubscription(activeSub.id)}
                        className="text-green-600 text-sm font-medium bg-white px-3 py-2 rounded-lg hover:bg-green-50 transition-all">
                        Resume Subscription
                      </button>
                      <button
                        onClick={() => handleCancelSubscription && handleCancelSubscription(activeSub.id)}
                        className="text-red-600 text-sm font-medium bg-white px-3 py-2 rounded-lg hover:bg-red-50 transition-all">
                        Cancel Subscription
                      </button>
                      <button
                        onClick={() => typeof handleModifySubscription === 'function' && handleModifySubscription(activeSub)}
                        className="text-blue-600 text-sm font-medium bg-white px-3 py-2 rounded-lg hover:bg-blue-50 transition-all">
                        Modify Plan
                      </button>
                    </>
                  ) : (
                    <>
                      <button 
                        onClick={() => handlePauseSubscription && handlePauseSubscription(activeSub.id)}
                        className="text-red-600 text-sm font-medium bg-white px-3 py-2 rounded-lg hover:bg-red-50 transition-all">
                        Pause Subscription
                      </button>
                      <button
                        onClick={() => handleCancelSubscription && handleCancelSubscription(activeSub.id)}
                        className="text-gray-700 text-sm font-medium bg-white px-3 py-2 rounded-lg hover:bg-gray-50 transition-all">
                        Cancel Subscription
                      </button>
                      <button
                        onClick={() => typeof handleModifySubscription === 'function' && handleModifySubscription(activeSub)}
                        className="text-blue-600 text-sm font-medium bg-white px-3 py-2 rounded-lg hover:bg-blue-50 transition-all">
                        Modify Plan
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })()
        )}

        {/* Menu Items */}    

        <div className="space-y-3">
          <button 
            onClick={() => setShowMyOrders(true)}
            className="w-full bg-white rounded-xl shadow-sm p-4 text-left flex items-center justify-between hover:bg-gray-50 transition-all"
          >
            <div className="flex items-center gap-3">
              <Package size={20} className="text-gray-600" />
              <span className="font-medium text-gray-700">My Orders</span>
            </div>
            <ChevronRight size={20} className="text-gray-400" />
          </button>

          <button 
            onClick={() => setShowFavorites(true)}
            className="w-full bg-white rounded-xl shadow-sm p-4 text-left flex items-center justify-between hover:bg-gray-50 transition-all"
          >
            <div className="flex items-center gap-3">
              <Heart size={20} className="text-gray-600" />
              <span className="font-medium text-gray-700">Favorites</span>
            </div>
            <ChevronRight size={20} className="text-gray-400" />
          </button>

          <button 
            onClick={() => setShowNotificationsModal(true)}
            className="w-full bg-white rounded-xl shadow-sm p-4 text-left flex items-center justify-between hover:bg-gray-50 transition-all"
          >
            <div className="flex items-center gap-3">
              <Bell size={20} className="text-gray-600" />
              <span className="font-medium text-gray-700">Notifications</span>
            </div>
            <ChevronRight size={20} className="text-gray-400" />
          </button>

          <button 
            onClick={() => setShowPaymentMethods(true)}
            className="w-full bg-white rounded-xl shadow-sm p-4 text-left flex items-center justify-between hover:bg-gray-50 transition-all"
          >
            <div className="flex items-center gap-3">
              <Package size={20} className="text-gray-600" />
              <span className="font-medium text-gray-700">Payment Methods</span>
            </div>
            <ChevronRight size={20} className="text-gray-400" />
          </button>
          {appSettings.securityAlertsEnabled  && (
          <button 
            onClick={() => setShowSecuritySettings(true)}
            className="w-full bg-white rounded-xl shadow-sm p-4 text-left flex items-center justify-between hover:bg-gray-50 transition-all"
          >
            <div className="flex items-center gap-3">
              <Shield size={20} className="text-blue-600" />
              <span className="font-medium text-gray-700">Security & Privacy</span>
            </div>
            <ChevronRight size={20} className="text-gray-400" />
          </button>
          )}
    
          <button 
            onClick={() => setShowPWAScreen(true)}
            className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl shadow-md p-4 text-left flex items-center justify-between hover:shadow-lg transition-all"
          >
            <div className="flex items-center gap-3">
              <Smartphone size={20} className="text-white" />
              <div>
                <span className="font-medium text-white block">App Settings (PWA)</span>
                <span className="text-xs text-indigo-100">Install, offline & notifications</span>
              </div>
            </div>
            <ChevronRight size={20} className="text-white" />
          </button>
          
          <button 
            onClick={() => setShowHelpModal(true)}
            className="w-full bg-white rounded-xl shadow-sm p-4 text-left flex items-center justify-between hover:bg-gray-50 transition-all">
            <div className="flex items-center gap-3">
              <MessageCircle size={20} className="text-gray-600" />
              <span className="font-medium text-gray-700">Help & Support</span>
            </div>
            <ChevronRight size={20} className="text-gray-400" />
          </button>
        </div>

        {/* App Info */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-400">Version {appSettings?.version || '1.0.0'}</p>
          <p className="text-xs text-gray-400 mt-1">© {new Date().getFullYear()} {appSettings?.appName || 'Milk Delivery App'}</p>
        </div>
      </div>

      {/* Help & Support Modal */}
      {showHelpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end z-50">
          <div className="bg-white w-full max-w-md mx-auto rounded-t-3xl p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Help & Support</h2>
              <button onClick={() => setShowHelpModal(false)} className="text-gray-500 hover:text-gray-700">
                ✕
              </button>
            </div>

            <div className="space-y-4">
              {/* Contact Options */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <h3 className="font-semibold text-gray-800 mb-3">Contact Us</h3>
                
                <button className="w-full bg-white rounded-lg p-3 mb-2 flex items-center gap-3 hover:bg-blue-50 transition-all">
                  <Phone size={20} className="text-blue-600" />
                  <div className="text-left">
                    <p className="font-medium text-gray-800">Call Us</p>
                    <p className="text-sm text-gray-600">({appSettings.supportContact || '+91 88266-00232'})</p>
                  </div>
                </button>

                <button className="w-full bg-white rounded-lg p-3 mb-2 flex items-center gap-3 hover:bg-blue-50 transition-all">
                  <MessageCircle size={20} className="text-green-600" />
                  <div className="text-left">
                    <p className="font-medium text-gray-800">WhatsApp Chat</p>
                    <p className="text-sm text-gray-600">Available 24/7</p>
                  </div>
                </button>

                <button className="w-full bg-white rounded-lg p-3 flex items-center gap-3 hover:bg-blue-50 transition-all">
                  <Mail size={20} className="text-red-600" />
                  <div className="text-left">
                    <p className="font-medium text-gray-800">Email Support</p>
                    <p className="text-sm text-gray-600">({appSettings.supportEmail || 'support@dealstore.com'})</p>
                  </div>
                </button>
              </div>

              {/* FAQ */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="font-semibold text-gray-800 mb-3">Frequently Asked Questions</h3>
                
                <div className="space-y-2">
                  <details className="bg-white rounded-lg p-3">
                    <summary className="font-medium text-gray-700 cursor-pointer">How do I track my order?</summary>
                    <p className="text-sm text-gray-600 mt-2">Go to Orders tab and click on your active order to see real-time tracking.</p>
                  </details>

                  <details className="bg-white rounded-lg p-3">
                    <summary className="font-medium text-gray-700 cursor-pointer">Can I pause my subscription?</summary>
                    <p className="text-sm text-gray-600 mt-2">Yes! Go to your profile and click on "Pause Subscription" under Active Subscription.</p>
                  </details>

                  <details className="bg-white rounded-lg p-3">
                    <summary className="font-medium text-gray-700 cursor-pointer">What are delivery timings?</summary>
                    <p className="text-sm text-gray-600 mt-2">We offer Morning (6-8 AM), Afternoon (2-4 PM), and Evening (6-8 PM) slots.</p>
                  </details>

                  <details className="bg-white rounded-lg p-3">
                    <summary className="font-medium text-gray-700 cursor-pointer">How do refunds work?</summary>
                    <p className="text-sm text-gray-600 mt-2">Refunds are processed within 5-7 business days to your original payment method.</p>
                  </details>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileScreen;